import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { Button, LoadingSpinner, ErrorState } from '../common';
import { CommentItem } from './CommentItem';
import { CommentComposer } from './CommentComposer';
import { useCommentThread } from '../../hooks/useCommentThread';
import { usePostsStore } from '../../store/postsStore';

interface InlineCommentsProps {
  postId: string;
  commentsCount: number;
}

/**
 * The comment thread rendered in the page, for the desktop shell.
 *
 * Below the shell a comment opens CommentsSheet, which is the native idiom
 * on a phone. On desktop that put a centered dialog over the photo while a
 * lookalike composer stayed docked at the bottom of the screen: two inputs,
 * one of them a decoy. Here the thread and the one real composer sit at the
 * end of the post, which is what the mockup's ask bar describes.
 *
 * The list is short and lives inside the screen's ScrollView, so it renders
 * plainly rather than through a nested FlatList (a virtualized list inside a
 * scroll view of the same axis never measures right on web).
 */
export function InlineComments({ postId, commentsCount }: InlineCommentsProps) {
  const { colors } = useTheme();
  const {
    comments,
    isLoading,
    isSending,
    loadError,
    commentText,
    setCommentText,
    editingCommentId,
    currentUserId,
    loadComments,
    handleSend,
    handleStartEdit,
    handleCancelEdit,
    handleDelete,
  } = useCommentThread(postId);
  // While a delete's undo toast is up the real decrement hasn't landed yet
  // (deferredCommit — see useCommentThread's handleDelete), so the header
  // would otherwise still count a comment the list below no longer shows.
  const pendingHiddenCount = usePostsStore(
    (s) => Object.keys(s.pendingHiddenCommentIds[postId] ?? {}).length
  );
  const displayCount = Math.max(0, commentsCount - pendingHiddenCount);

  return (
    <View style={[styles.section, { borderTopColor: colors.border }]} testID="inline-comments">
      <Text style={[styles.heading, { color: colors.text }]}>
        Comments{displayCount > 0 ? ` (${displayCount})` : ''}
      </Text>

      {isLoading ? (
        <View style={styles.state}>
          <LoadingSpinner message="Loading comments..." />
        </View>
      ) : loadError && comments.length === 0 ? (
        <View style={styles.state}>
          <ErrorState message={loadError} onRetry={loadComments} />
        </View>
      ) : comments.length === 0 ? (
        <Text style={[styles.empty, { color: colors.textSecondary }]}>
          No comments yet. Be the first to comment.
        </Text>
      ) : (
        <View style={styles.list}>
          {loadError && (
            <View style={[styles.loadMoreError, { backgroundColor: colors.dangerSoft }]}>
              <Text style={[styles.loadMoreErrorText, { color: colors.danger }]}>{loadError}</Text>
              <Button title="Retry" onPress={loadComments} variant="ghost" size="sm" />
            </View>
          )}
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              onEdit={handleStartEdit}
              onDelete={handleDelete}
            />
          ))}
        </View>
      )}

      <CommentComposer
        value={commentText}
        onChangeText={setCommentText}
        onSend={handleSend}
        isSending={isSending}
        isEditing={Boolean(editingCommentId)}
        onCancelEdit={handleCancelEdit}
        style={styles.composer}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    // The screen's contentConstraint carries no padding — each block insets
    // itself (see rowLink). Padding rather than margin, so this section's
    // opening rule spans the column like the row-link rules above it while
    // the content stays inset: as a margin it was a short rule among full
    // ones. The inset is what keeps the composer's rounded right edge off
    // the viewport, so it has to stay one way or the other.
    marginTop: SPACING.lg,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: 1,
  },
  heading: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.xs,
  },
  state: {
    paddingVertical: SPACING.lg,
  },
  empty: {
    ...TYPOGRAPHY.bodySmall,
    paddingVertical: SPACING.smd,
  },
  list: {
    paddingBottom: SPACING.xs,
  },
  loadMoreError: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    marginBottom: SPACING.sm,
    borderRadius: 12,
  },
  loadMoreErrorText: {
    flex: 1,
    ...TYPOGRAPHY.label,
  },
  composer: {
    marginTop: SPACING.sm,
  },
});
